import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { AlertIconType, useBlocker } from '@pbt/pbt-ui-components'

import { ContextualBackButton } from '~/components/common/buttons/ContextualBackButton'
import InvoiceBody, {
  type InvoiceBodyHandle,
} from '~/components/dashboard/invoices/InvoiceBody'
import useEstimateAlertDialog from '~/components/dashboard/soapV2/estimates/utils/useEstimateAlertDialog'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { ClientAndPatientRoute } from '~/constants/routes'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getFinanceIsSaving } from '~/store/reducers/finance'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import useBeforeUnload from '~/utils/useBeforeUnload'
import useDialog from '~/utils/useDialog'

import styles from './CreateEstimatePage.module.scss'

type BlockerTransition = {
  retry: () => void
}

const CreateEstimatePage = () => {
  const { t } = useTranslation(['Clients', 'Dialogs', 'Common'])
  const navigate = useNavigate()
  const { clientId = '', patientId = '' } = useParams<{
    clientId: string
    patientId: string
  }>()

  const isAwoChargesR5Epic3Enabled = useSelector(
    getFeatureToggle(FeatureToggle.AWO_CHARGES_R5_3),
  )

  const client = useSelector(getUser(clientId))
  const patient = useSelector(getPatient(patientId))
  const isSaving = useSelector(getFinanceIsSaving)

  const shellRef = useRef<InvoiceBodyHandle>(null)
  const pendingBlockedTransition = useRef<BlockerTransition | null>(null)
  /** When true, the next `useBlocker` callback is finishing a leave we already started via `attemptClose` (no pending transition); only `retry()`, do not call `attemptClose` again. */
  const skipShellAttemptCloseForNextBlockerRef = useRef(false)

  const { setAttachingToSoapEstimateId, setSoapToAttachEstimateId } =
    useEstimateAlertDialog({
      fromSoap: false,
      fromTimeline: false,
      fromOther: true,
    })

  const [openUnsavedNavigationDialog, closeUnsavedNavigationDialog] = useDialog(
    DialogNames.DISMISSIBLE_ALERT,
  )

  const snapshotsPath = `/client/${clientId}/patient/${patientId}/${ClientAndPatientRoute.SNAPSHOTS}`

  const completeLeave = useCallback(() => {
    const tx = pendingBlockedTransition.current
    pendingBlockedTransition.current = null
    if (tx) {
      tx.retry()
    } else {
      navigate(snapshotsPath)
    }
  }, [navigate, snapshotsPath])

  const onShellClose = useCallback(
    (_redirectTo?: string) => {
      completeLeave()
    },
    [completeLeave],
  )

  const handleBlockedNavigation = useCallback(
    (tx: BlockerTransition) => {
      if (!shellRef.current?.getHasUnsavedChanges()) {
        if (skipShellAttemptCloseForNextBlockerRef.current) {
          skipShellAttemptCloseForNextBlockerRef.current = false
          tx.retry()
          return
        }
        const shell = shellRef.current
        if (shell) {
          pendingBlockedTransition.current = tx
          shell.attemptClose('')
        } else {
          tx.retry()
        }
        return
      }
      pendingBlockedTransition.current = tx
      openUnsavedNavigationDialog({
        cancelButtonText: t('Common:CANCEL_ACTION'),
        iconType: AlertIconType.WARN,
        message: t('Dialogs:LINK_INDIVIDUAL_PATIENT_DIALOG.EXIT_TITLE'),
        okButtonText: t('Common:LEAVE_ANYWAY_ACTION'),
        onCancel: () => {
          pendingBlockedTransition.current = null
          closeUnsavedNavigationDialog()
        },
        onClose: () => {
          pendingBlockedTransition.current = null
          closeUnsavedNavigationDialog()
        },
        onOk: () => {
          closeUnsavedNavigationDialog()
          if (clientId && patientId) {
            skipShellAttemptCloseForNextBlockerRef.current = true
          }
          shellRef.current?.attemptClose('')
        },
      })
    },
    [
      clientId,
      closeUnsavedNavigationDialog,
      openUnsavedNavigationDialog,
      patientId,
      t,
    ],
  )

  useBlocker(handleBlockedNavigation, Boolean(clientId && patientId))

  useBeforeUnload(() => Boolean(shellRef.current?.getHasUnsavedChanges()))

  const handleContextualBack = useCallback(() => {
    if (isSaving) {
      return
    }
    if (shellRef.current?.getHasUnsavedChanges()) {
      pendingBlockedTransition.current = null
      openUnsavedNavigationDialog({
        cancelButtonText: t('Common:CANCEL_ACTION'),
        iconType: AlertIconType.WARN,
        message: t('Dialogs:LINK_INDIVIDUAL_PATIENT_DIALOG.EXIT_TITLE'),
        okButtonText: t('Common:LEAVE_ANYWAY_ACTION'),
        onCancel: closeUnsavedNavigationDialog,
        onClose: closeUnsavedNavigationDialog,
        onOk: () => {
          closeUnsavedNavigationDialog()
          if (clientId && patientId) {
            skipShellAttemptCloseForNextBlockerRef.current = true
          }
          shellRef.current?.attemptClose('')
        },
      })
      return
    }
    if (clientId && patientId) {
      skipShellAttemptCloseForNextBlockerRef.current = true
    }
    shellRef.current?.attemptClose('')
  }, [
    clientId,
    closeUnsavedNavigationDialog,
    isSaving,
    openUnsavedNavigationDialog,
    patientId,
    t,
  ])

  if (!isAwoChargesR5Epic3Enabled) {
    return <Navigate replace to={snapshotsPath} />
  }

  if (!client || !patient) {
    return null
  }

  return (
    <div className={styles.root}>
      <div className={styles.topBar}>
        <ContextualBackButton
          buttonText={t(
            'Clients:SNAPSHOTS_AND_RECORDS.PATIENT_SNAPSHOTS_LABEL',
          )}
          navigateBackToContext={handleContextualBack}
        />
      </div>
      <div className={styles.main}>
        <InvoiceBody
          create
          isEstimate
          open
          clientId={clientId}
          patientId={patientId}
          ref={shellRef}
          setAttachingToSoapEstimateId={setAttachingToSoapEstimateId}
          setSoapToAttachEstimateId={setSoapToAttachEstimateId}
          onClose={onShellClose}
        />
      </div>
    </div>
  )
}

export default CreateEstimatePage
